import * as React from "react";
import { useMemo } from "react";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import { useSearchParams } from "react-router-dom";
import NeedAttentionFetcher from "./NeedAttentionFetcher";
import NeedAttentionUnifier from "./NeedAttentionUnifier";
import NeedAttentionAdvisor from "./NeedAttentionAdvisor";
import {
  Autocomplete,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

interface Props {
  familyRows: any[];
}

const STEP_KEYS = ["fetcher", "unifier", "advisor"];

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const NeedAttentionTabs = ({ familyRows }: Props) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const value = Math.max(0, STEP_KEYS.indexOf(searchParams.get("step") ?? "fetcher"));
  const searchTerm = searchParams.get("familySearch") ?? "";

  const handleChange = (_: React.SyntheticEvent, newValue: number) => {
    const next = new URLSearchParams(searchParams);
    if (newValue === 0) {
      next.delete("step");
    } else {
      next.set("step", STEP_KEYS[newValue]);
    }
    setSearchParams(next, { replace: true });
  };

  const setSearchTerm = (val: string) => {
    const next = new URLSearchParams(searchParams);
    if (val) {
      next.set("familySearch", val);
    } else {
      next.delete("familySearch");
    }
    setSearchParams(next, { replace: true });
  };

  const TAB_LABELS = ["Fetcher", "Unifier", "Advisor"];
  // Current selected tab name
  const selectedTab = TAB_LABELS[value];

  const filteredRows = useMemo(
    () =>
      searchTerm
        ? familyRows.filter((row: any) =>
            row.ui_family_name?.toLowerCase().includes(searchTerm.toLowerCase()),
          )
        : familyRows,
    [familyRows, searchTerm],
  );

  const searchOptions = useMemo(() => {
    const uniqueNames = Array.from(
      new Set(
        familyRows
          .map((row: any) => row.ui_family_name)
          .filter((name: any) => !!name),
      ),
    );
    return uniqueNames.map((name) => ({ label: name }));
  }, [familyRows]);

  const fetcherRows = useMemo(
    () => filteredRows.filter((row: any) => row.step === "fetcher"),
    [filteredRows],
  );
  const unifierRows = useMemo(
    () => filteredRows.filter((row: any) => row.step === "unifier"),
    [filteredRows],
  );
  const advisorRows = useMemo(
    () => filteredRows.filter((row: any) => row.step === "advisor"),
    [filteredRows],
  );

  return (
    <>
      <Box sx={{ width: "100%" }}>
        <Box
          sx={{
            display: "flex",
            gap: "16px",
            width: "100%",
            alignItems: "flex-start",
            justifyContent: "space-between",
            padding: "0px 16px 20px",
          }}
        >
          <div>
            <Tabs
              value={value}
              onChange={handleChange}
              sx={{
                minHeight: 0,
                backgroundColor: "#F5F5F5",
                borderRadius: "8px",
                padding: "4px",
                display: "inline-flex",

                /* Hide the default underline indicator entirely */
                "& .MuiTabs-indicator": {
                  display: "none",
                },

                "& .MuiTabs-flexContainer": {
                  gap: "4px",
                },

                "& .MuiTab-root": {
                  minHeight: 0,
                  padding: "8px 16px",
                  borderRadius: "4px",
                  textTransform: "uppercase",
                  fontSize: "13px",
                  fontWeight: 500,
                  color: "#00000061",
                  transition: "all 0.15s ease",

                  "&.Mui-selected": {
                    color: "#000000DE",
                    backgroundColor: "#FFFFFF",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
                  },

                  "&:hover": {
                    backgroundColor: "#FFFFFF",
                  },
                },
              }}
            >
              <Tab label="FETCHER" {...a11yProps(0)} />
              <Tab label="UNIFIER" {...a11yProps(1)} />
              <Tab label="ADVISOR" {...a11yProps(2)} />
            </Tabs>
            <Typography
              sx={{ color: "#00000099", fontSize: "12px", mt: "8px" }}
              variant="body2"
            >
              Empty = zero brands. Missing maps/configs = a family that won't
              process anything until one is attached.
            </Typography>
          </div>
          <div data-tracker="1017">
            <Autocomplete
              sx={{ width: "300px" }}
              options={searchOptions}
              freeSolo
              inputValue={searchTerm}
              onInputChange={(_, val) => setSearchTerm(val)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search Family Name"
                  label="Search Family Name"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ marginLeft: "12px" }} />
                      </InputAdornment>
                    ),
                  }}
                  color="secondary"
                />
              )}
              size="small"
            />
          </div>
        </Box>

        <CustomTabPanel value={value} index={0}>
          <NeedAttentionFetcher speciesTitle={selectedTab} rows={fetcherRows} />
        </CustomTabPanel>
        <CustomTabPanel value={value} index={1}>
          <NeedAttentionUnifier speciesTitle={selectedTab} rows={unifierRows} />
        </CustomTabPanel>
        <CustomTabPanel value={value} index={2}>
          <NeedAttentionAdvisor speciesTitle={selectedTab} rows={advisorRows} />
        </CustomTabPanel>
      </Box>
    </>
  );
};
  
export default NeedAttentionTabs;
